<script setup>
// Estúdio Social → Métricas (F2). Relatório de UMA rede (Instagram,
// Facebook ou X) no período: tiles com comparação, alcance por dia, seguidores
// ganhos×perdidos, melhores horários, ranking de posts, Stories, Reels e
// demografia. Contrato: GET /social/metrics/overview → { accounts,
// selected_id, report } (Api::V1::Accounts::Social::MetricsController).
// Ausente é "—", nunca zero; parte que a Meta não libera vira aviso.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { useAccount } from 'dashboard/composables/useAccount';
import { useDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';
import { useAlert } from 'dashboard/composables';
import socialAPI from 'dashboard/api/social';
import Button from 'dashboard/components-next/button/Button.vue';
import SocialNav from './SocialNav.vue';
import StatTile from './metrics/StatTile.vue';
import TrendChart from './metrics/TrendChart.vue';
import FollowerFlowChart from './metrics/FollowerFlowChart.vue';
import BestTimeHeatmap from './metrics/BestTimeHeatmap.vue';
import BarList from './metrics/BarList.vue';
import {
  deltaPercent,
  formatNumber,
  topDimensions,
} from './metrics/metricsHelpers';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { dateFnsLocale } = useDateFnsLocale();

const PERIODS = [7, 28, 90];
const isLoading = ref(true);
const loadError = ref(false);
const accounts = ref([]);
const selectedId = ref(null);
const days = ref(28);
const report = ref(null);

const fetchOverview = async () => {
  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } = await socialAPI.metricsOverview({
      socialAccountId: selectedId.value,
      days: days.value,
    });
    accounts.value = data.accounts || [];
    selectedId.value = data.selected_id;
    report.value = data.report;
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const choosePeriod = value => {
  days.value = value;
  fetchOverview();
};
const chooseAccount = event => {
  selectedId.value = Number(event.target.value);
  fetchOverview();
};

const selectedAccount = computed(() =>
  accounts.value.find(item => item.id === selectedId.value)
);
// X cobra cada leitura: não atualiza sozinho e o botão mostra o preço; o
// clique lê na hora (o backend debita dos créditos) e recarrega o relatório.
const isPaidRefresh = computed(() => !!selectedAccount.value?.paid_refresh);
const formatBrl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value || 0
  );
const refreshLabel = computed(() =>
  isPaidRefresh.value
    ? t('SOCIAL_STUDIO.METRICS.REFRESH_X', {
        price: formatBrl(selectedAccount.value.refresh_price_brl),
      })
    : t('SOCIAL_STUDIO.METRICS.REFRESH')
);

const refreshErrorMessage = error => {
  const status = error.response?.status;
  if (status === 429) return t('SOCIAL_STUDIO.METRICS.REFRESH_COOLDOWN');
  if (status === 402 && error.response?.data?.error) {
    return error.response.data.error;
  }
  return t('SOCIAL_STUDIO.METRICS.LOADING_ERROR');
};

const refresh = async () => {
  try {
    if (isPaidRefresh.value) {
      await socialAPI.refreshMetrics({ socialAccountId: selectedId.value });
      useAlert(t('SOCIAL_STUDIO.METRICS.REFRESH_X_DONE'));
      await fetchOverview();
      return;
    }
    await socialAPI.refreshMetrics();
    useAlert(t('SOCIAL_STUDIO.METRICS.REFRESH_QUEUED'));
  } catch (error) {
    useAlert(refreshErrorMessage(error));
  }
};

onMounted(fetchOverview);

const isInstagram = computed(
  () => selectedAccount.value?.platform === 'instagram'
);
const tiles = computed(() => report.value?.tiles || {});
const series = computed(() => report.value?.series || { dates: [] });
const live = computed(() => report.value?.live || {});
const audienceKey = computed(() =>
  report.value?.audience_metric === 'views' ? 'VIEWS' : 'REACH'
);
// Alcance diário somado não é alcance único: quando a Meta não entregou o
// total do período, o tile diz que é soma (unique: false vem do backend).
const audienceTileLabel = computed(() => {
  if (audienceKey.value === 'REACH' && tiles.value.audience?.unique === false) {
    return t('SOCIAL_STUDIO.METRICS.TILES.REACH_SUMMED');
  }
  return t(`SOCIAL_STUDIO.METRICS.TILES.${audienceKey.value}`);
});
const comparisonLabel = computed(() =>
  t('SOCIAL_STUDIO.METRICS.VS_PREVIOUS', { days: days.value })
);

// Seguidores por hora (só Instagram): saldo de cada hora das últimas 48 h.
const hourly = computed(() => report.value?.hourly_followers || null);
const hourlyNetLabel = computed(() => {
  const net =
    (hourly.value?.gained || []).reduce((sum, value) => sum + value, 0) -
    (hourly.value?.lost || []).reduce((sum, value) => sum + value, 0);
  return net > 0 ? `+${net}` : String(net);
});
const hourLabel = iso =>
  iso
    ? format(parseISO(iso), "dd/MM HH'h'", { locale: dateFnsLocale.value })
    : '';

const shortDate = iso =>
  iso ? format(parseISO(iso), 'dd/MM', { locale: dateFnsLocale.value }) : '';
const longDate = iso =>
  iso ? format(parseISO(iso), 'PP', { locale: dateFnsLocale.value }) : '';
const lastSync = computed(() => {
  const raw = selectedAccount.value?.metrics_synced_at;
  if (!raw) return t('SOCIAL_STUDIO.METRICS.NEVER_SYNCED');
  return t('SOCIAL_STUDIO.METRICS.LAST_SYNC', {
    time: formatDistanceToNow(parseISO(raw), {
      addSuffix: true,
      locale: dateFnsLocale.value,
    }),
  });
});
const percent = value => (value == null ? '—' : `${formatNumber(value)}%`);
const seconds = ms => (ms == null ? '—' : `${formatNumber(ms / 1000)} s`);

// URLs do CDN do Instagram expiram (~5 dias): quando a <img> falhar, some com
// ela e mostra o placeholder no lugar em vez de deixar o ícone quebrado.
const failedImages = ref(new Set());
const isImageFailed = id => failedImages.value.has(id);
const markImageFailed = id => failedImages.value.add(id);

const dayLabels = computed(() =>
  [0, 1, 2, 3, 4, 5, 6].map(day => t(`SOCIAL_STUDIO.METRICS.DAYS.${day}`))
);
const demographics = computed(() => {
  const data = live.value.demographics;
  if (!data) return null;
  const toItems = (list, labelFor = item => item.dimension) =>
    topDimensions(list || [], 6).map(item => ({
      label: labelFor(item),
      value: item.value,
      share: item.share,
    }));
  return {
    age: toItems(data.age),
    gender: toItems(data.gender, item =>
      t(`SOCIAL_STUDIO.METRICS.DEMOGRAPHICS.GENDERS.${item.dimension}`)
    ),
    city: toItems(data.city),
  };
});
const profileTotals = computed(() => live.value.totals);
// Conectar rede fica em Canais → Adicionar Canal (decisão Ivan 08/10).
const goConnect = () => router.push(accountScopedRoute('settings_inbox_new'));
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-auto bg-n-background">
    <SocialNav />
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6">
      <header class="flex flex-wrap items-end justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.METRICS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('SOCIAL_STUDIO.METRICS.SUBTITLE') }}
          </p>
        </div>
        <div v-if="accounts.length" class="flex flex-wrap items-center gap-3">
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            {{ t('SOCIAL_STUDIO.METRICS.ACCOUNT_LABEL') }}
            <select
              id="social-metrics-account"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-sm text-n-slate-12"
              :value="selectedId"
              @change="chooseAccount"
            >
              <option v-for="item in accounts" :key="item.id" :value="item.id">
                {{ item.display_name || item.username }} · {{ item.platform }}
              </option>
            </select>
          </label>
          <div
            class="flex overflow-hidden rounded-lg border border-n-weak"
            role="group"
            :aria-label="t('SOCIAL_STUDIO.METRICS.PERIOD_LABEL')"
          >
            <button
              v-for="period in PERIODS"
              :key="period"
              type="button"
              :data-test="`period-${period}`"
              class="px-3 py-1 text-sm"
              :class="
                period === days
                  ? 'bg-n-brand text-white'
                  : 'bg-n-solid-1 text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="choosePeriod(period)"
            >
              {{ t(`SOCIAL_STUDIO.METRICS.PERIODS.${period}`) }}
            </button>
          </div>
          <Button
            size="sm"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            data-test="metrics-refresh"
            :label="refreshLabel"
            @click="refresh"
          />
          <span class="text-xs text-n-slate-10">{{ lastSync }}</span>
        </div>
      </header>

      <div v-if="isLoading" class="py-20 text-center text-sm text-n-slate-11">
        {{ '…' }}
      </div>

      <div v-else-if="loadError" class="flex flex-col items-center gap-3 py-20">
        <p class="text-sm text-n-slate-11">
          {{ t('SOCIAL_STUDIO.METRICS.LOADING_ERROR') }}
        </p>
        <Button
          size="sm"
          :label="t('SOCIAL_STUDIO.METRICS.RETRY')"
          @click="fetchOverview"
        />
      </div>

      <div
        v-else-if="!report"
        class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-n-weak py-16 text-center"
      >
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('SOCIAL_STUDIO.METRICS.EMPTY.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('SOCIAL_STUDIO.METRICS.EMPTY.SUBTITLE') }}
        </p>
        <Button
          size="sm"
          :label="t('SOCIAL_STUDIO.METRICS.EMPTY.CTA')"
          @click="goConnect"
        />
      </div>

      <template v-else>
        <p
          v-if="isPaidRefresh"
          class="rounded-lg bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
        >
          {{
            t('SOCIAL_STUDIO.METRICS.X_PAID_NOTICE', {
              price: formatBrl(selectedAccount.refresh_price_brl),
            })
          }}
        </p>
        <p class="rounded-lg bg-n-slate-2 px-3 py-2 text-xs text-n-slate-11">
          {{
            report.history_starts_on
              ? t('SOCIAL_STUDIO.METRICS.HISTORY_STARTS', {
                  date: longDate(report.history_starts_on),
                })
              : t('SOCIAL_STUDIO.METRICS.HISTORY_EMPTY')
          }}
        </p>

        <section class="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatTile
            :label="t('SOCIAL_STUDIO.METRICS.TILES.FOLLOWERS')"
            :value="tiles.followers?.value"
          />
          <StatTile
            :label="audienceTileLabel"
            :value="tiles.audience?.value"
            :delta="
              deltaPercent(tiles.audience?.value, tiles.audience?.previous)
            "
            :delta-label="comparisonLabel"
          />
          <StatTile
            :label="t('SOCIAL_STUDIO.METRICS.TILES.FOLLOWERS_NET')"
            :value="tiles.followers_net?.value"
            :delta="
              deltaPercent(
                tiles.followers_net?.value,
                tiles.followers_net?.previous
              )
            "
            :delta-label="comparisonLabel"
          />
          <StatTile
            :label="t('SOCIAL_STUDIO.METRICS.TILES.POSTS')"
            :value="tiles.posts?.value"
            :delta="deltaPercent(tiles.posts?.value, tiles.posts?.previous)"
            :delta-label="comparisonLabel"
          />
          <StatTile
            :label="t('SOCIAL_STUDIO.METRICS.TILES.ENGAGEMENT_RATE')"
            :value="tiles.engagement_rate?.value"
            :format="percent"
            :delta="
              deltaPercent(
                tiles.engagement_rate?.value,
                tiles.engagement_rate?.previous
              )
            "
            :delta-label="comparisonLabel"
          />
        </section>

        <section class="grid gap-4 lg:grid-cols-2">
          <div
            class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t(`SOCIAL_STUDIO.METRICS.CHARTS.AUDIENCE_${audienceKey}`) }}
            </h2>
            <TrendChart
              :dates="series.dates"
              :values="series.audience || []"
              :label="t(`SOCIAL_STUDIO.METRICS.CHARTS.AUDIENCE_${audienceKey}`)"
              :format-date="shortDate"
            />
          </div>
          <div
            class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.METRICS.CHARTS.FOLLOWER_FLOW') }}
            </h2>
            <FollowerFlowChart
              :dates="series.dates"
              :gained="series.followers_gained || []"
              :lost="series.followers_lost || []"
              :gained-label="t('SOCIAL_STUDIO.METRICS.CHARTS.GAINED')"
              :lost-label="t('SOCIAL_STUDIO.METRICS.CHARTS.LOST')"
              :format-date="shortDate"
            />
          </div>
        </section>

        <!-- Seguidores por hora: leitura do total pela caixa do Instagram -->
        <section
          v-if="hourly"
          class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          data-test="hourly-followers"
        >
          <div class="flex flex-wrap items-baseline justify-between gap-2">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.METRICS.HOURLY.TITLE') }}
            </h2>
            <span
              v-if="hourly.hours.length"
              class="text-xs tabular-nums text-n-slate-11"
            >
              {{
                t('SOCIAL_STUDIO.METRICS.HOURLY.SUMMARY', {
                  net: hourlyNetLabel,
                  current: hourly.current,
                })
              }}
            </span>
          </div>
          <FollowerFlowChart
            v-if="hourly.hours.length"
            :dates="hourly.hours"
            :gained="hourly.gained"
            :lost="hourly.lost"
            :gained-label="t('SOCIAL_STUDIO.METRICS.CHARTS.GAINED')"
            :lost-label="t('SOCIAL_STUDIO.METRICS.CHARTS.LOST')"
            :format-date="hourLabel"
          />
          <p v-else class="text-sm text-n-slate-11">
            {{
              hourly.available
                ? t('SOCIAL_STUDIO.METRICS.HOURLY.COLLECTING')
                : t('SOCIAL_STUDIO.METRICS.HOURLY.NEEDS_INBOX')
            }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ t('SOCIAL_STUDIO.METRICS.HOURLY.HINT') }}
          </p>
        </section>

        <section
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.TITLE') }}
          </h2>
          <p v-if="!report.top_posts.length" class="text-sm text-n-slate-11">
            {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.EMPTY') }}
          </p>
          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[640px] text-sm">
              <thead
                class="text-left text-xs uppercase tracking-wide text-n-slate-11"
              >
                <tr>
                  <th class="py-2 pr-3" />
                  <th class="py-2 pr-3 text-right">
                    {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.ENGAGEMENT') }}
                  </th>
                  <th class="py-2 pr-3 text-right">
                    {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.REACH') }}
                  </th>
                  <th class="py-2 pr-3 text-right">
                    {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.LIKES') }}
                  </th>
                  <th class="py-2 pr-3 text-right">
                    {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.COMMENTS') }}
                  </th>
                  <th class="py-2 pr-3 text-right">
                    {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.SAVES') }}
                  </th>
                  <th class="py-2 text-right">
                    {{ t('SOCIAL_STUDIO.METRICS.TOP_POSTS.FOLLOWS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="post in report.top_posts"
                  :key="post.id"
                  class="border-t border-n-weak"
                >
                  <td class="py-2 pr-3">
                    <a
                      :href="post.permalink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-3 hover:underline"
                    >
                      <img
                        v-if="
                          post.thumbnail_url &&
                          !isImageFailed(`post-${post.id}`)
                        "
                        :src="post.thumbnail_url"
                        alt=""
                        class="size-10 shrink-0 rounded object-cover"
                        loading="lazy"
                        @error="markImageFailed(`post-${post.id}`)"
                      />
                      <span
                        v-else-if="post.thumbnail_url"
                        class="flex size-10 shrink-0 items-center justify-center rounded bg-n-slate-3"
                      >
                        <span class="i-lucide-image size-4 text-n-slate-9" />
                      </span>
                      <span class="flex min-w-0 flex-col">
                        <span class="truncate text-n-slate-12">{{
                          post.content || post.media_product_type
                        }}</span>
                        <span class="text-xs text-n-slate-10">
                          {{ longDate(post.published_at) }} ·
                          {{
                            post.external
                              ? t('SOCIAL_STUDIO.METRICS.TOP_POSTS.EXTERNAL')
                              : t('SOCIAL_STUDIO.METRICS.TOP_POSTS.STUDIO')
                          }}
                        </span>
                      </span>
                    </a>
                  </td>
                  <td
                    class="py-2 pr-3 text-right tabular-nums font-semibold text-n-slate-12"
                  >
                    {{ percent(post.engagement_rate) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums">
                    {{ formatNumber(post.reach ?? post.views) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums">
                    {{ formatNumber(post.likes) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums">
                    {{ formatNumber(post.comments) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums">
                    {{ formatNumber(post.saves) }}
                  </td>
                  <td class="py-2 text-right tabular-nums">
                    {{ formatNumber(post.follows) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="grid gap-4 lg:grid-cols-3">
          <div
            class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 lg:col-span-2"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.METRICS.CHARTS.BEST_TIME') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('SOCIAL_STUDIO.METRICS.CHARTS.BEST_TIME_HINT') }}
            </p>
            <BestTimeHeatmap
              v-if="live.best_time?.length"
              :slots="live.best_time"
              :day-labels="dayLabels"
            />
            <p v-else class="text-sm text-n-slate-11">
              {{ t('SOCIAL_STUDIO.METRICS.CHARTS.BEST_TIME_EMPTY') }}
            </p>
          </div>
          <div
            class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.METRICS.REELS.TITLE') }}
            </h2>
            <p v-if="!report.reels.count" class="text-sm text-n-slate-11">
              {{ t('SOCIAL_STUDIO.METRICS.REELS.EMPTY') }}
            </p>
            <dl v-else class="grid gap-3 text-sm">
              <div class="flex justify-between">
                <dt class="text-n-slate-11">
                  {{ t('SOCIAL_STUDIO.METRICS.REELS.COUNT') }}
                </dt>
                <dd class="tabular-nums">{{ report.reels.count }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-n-slate-11">
                  {{ t('SOCIAL_STUDIO.METRICS.REELS.AVG_WATCH') }}
                </dt>
                <dd class="tabular-nums">
                  {{ seconds(report.reels.avg_watch_time_ms) }}
                </dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-n-slate-11">
                  {{ t('SOCIAL_STUDIO.METRICS.REELS.SKIP_RATE') }}
                </dt>
                <dd class="tabular-nums">
                  {{ percent(report.reels.skip_rate) }}
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section
          v-if="isInstagram"
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.METRICS.STORIES.TITLE') }}
          </h2>
          <p v-if="!report.stories.length" class="text-sm text-n-slate-11">
            {{ t('SOCIAL_STUDIO.METRICS.STORIES.EMPTY') }}
          </p>
          <ul v-else class="flex gap-3 overflow-x-auto pb-1">
            <li
              v-for="story in report.stories"
              :key="story.id"
              class="flex w-32 shrink-0 flex-col gap-1 text-xs"
            >
              <a
                :href="story.permalink"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  v-if="
                    story.thumbnail_url && !isImageFailed(`story-${story.id}`)
                  "
                  :src="story.thumbnail_url"
                  alt=""
                  class="aspect-[9/16] w-full rounded-lg object-cover"
                  loading="lazy"
                  @error="markImageFailed(`story-${story.id}`)"
                />
                <span
                  v-else
                  class="flex aspect-[9/16] w-full items-center justify-center rounded-lg bg-n-slate-3"
                >
                  <span class="i-lucide-image size-5 text-n-slate-9" />
                </span>
              </a>
              <span class="text-n-slate-10">{{
                longDate(story.published_at)
              }}</span>
              <span class="flex justify-between">
                <span class="text-n-slate-11">{{
                  t('SOCIAL_STUDIO.METRICS.STORIES.VIEWS')
                }}</span>
                <span class="tabular-nums">{{
                  formatNumber(story.views)
                }}</span>
              </span>
              <span class="flex justify-between">
                <span class="text-n-slate-11">{{
                  t('SOCIAL_STUDIO.METRICS.STORIES.REPLIES')
                }}</span>
                <span class="tabular-nums">{{
                  formatNumber(story.replies)
                }}</span>
              </span>
              <span class="flex justify-between">
                <span class="text-n-slate-11">{{
                  t('SOCIAL_STUDIO.METRICS.STORIES.EXITS')
                }}</span>
                <span class="tabular-nums">{{
                  formatNumber(story.exits)
                }}</span>
              </span>
            </li>
          </ul>
        </section>

        <section v-if="isInstagram" class="grid gap-4 lg:grid-cols-2">
          <div
            class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.METRICS.DEMOGRAPHICS.TITLE') }}
            </h2>
            <p v-if="!demographics" class="text-sm text-n-slate-11">
              {{ t('SOCIAL_STUDIO.METRICS.DEMOGRAPHICS.UNAVAILABLE') }}
            </p>
            <div v-else class="grid gap-4 md:grid-cols-3">
              <div class="flex flex-col gap-2">
                <h3 class="text-xs font-medium text-n-slate-11">
                  {{ t('SOCIAL_STUDIO.METRICS.DEMOGRAPHICS.AGE') }}
                </h3>
                <BarList :items="demographics.age" />
              </div>
              <div class="flex flex-col gap-2">
                <h3 class="text-xs font-medium text-n-slate-11">
                  {{ t('SOCIAL_STUDIO.METRICS.DEMOGRAPHICS.GENDER') }}
                </h3>
                <BarList :items="demographics.gender" />
              </div>
              <div class="flex flex-col gap-2">
                <h3 class="text-xs font-medium text-n-slate-11">
                  {{ t('SOCIAL_STUDIO.METRICS.DEMOGRAPHICS.CITY') }}
                </h3>
                <BarList :items="demographics.city" />
              </div>
            </div>
          </div>
          <div
            class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ t('SOCIAL_STUDIO.METRICS.PROFILE.TITLE') }}
            </h2>
            <dl class="grid grid-cols-2 gap-3 text-sm">
              <div
                v-for="key in [
                  'views',
                  'accounts_engaged',
                  'total_interactions',
                  'profile_links_taps',
                ]"
                :key="key"
                class="flex flex-col"
              >
                <dt class="text-xs text-n-slate-11">
                  {{ t(`SOCIAL_STUDIO.METRICS.PROFILE.${key.toUpperCase()}`) }}
                </dt>
                <dd class="text-lg font-semibold tabular-nums text-n-slate-12">
                  {{ formatNumber(profileTotals?.[key]) }}
                </dd>
              </div>
            </dl>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>
